import { ConversionRateTypes } from 'constants/conversionTypes';

import { useState } from 'react';

import { CumulativeReportResponse } from 'config/requests/cumulativeReport';
import {
  CumulativeReportFilterValues,
  CumulativeReportFilter,
} from 'pages/Finance/CumulativeReport/CumulativeReportFilter';
import { CumulativeReportListFilterChips } from 'pages/Finance/CumulativeReport/CumulativeReportListFilterChips';
import { ListContextProvider, useList, useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { SuccessReportConvertedAlert } from 'shared/mui/Alert/SuccessReportAlert/SuccessReportConvertedAlert';
import { DateRangeValue } from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { CumulativeReportCollapsibleSkeleton } from './components/CumulativeReportCollapsibleSkeleton';
import { CumulativeReportDatagridCollapsible } from './components/CumulativeReportDatagrid/CumulativeReportDatagridCollapsible';
import { CumulativeReportSummaryCollapsible } from './components/CumulativeReportSummary/CumulativeReportSummaryCollapsible';

type Props = {
  cumulativeReport: CumulativeReportResponse | undefined;
  isLoading: boolean;
};

const DEFAULT_CUMULATIVE_REPORT_FILTER_VALUES = {
  appliedDate: [null, null] as DateRangeValue,
  timezone: null,
  company: null,
  merchant: null,
  currency: null,
  provider: null,
  merchantAccounts: [],
  accountsSummary: null,
};

export const CumulativeReportListContent: React.FC<Props> = ({ cumulativeReport, isLoading }) => {
  const { filterValues, data } = useListContext();

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<CumulativeReportFilterValues>({
      mode: 'all',
      defaultValues: {
        appliedDate: [
          formatInputToTimestamp(filterValues?.periodFrom),
          formatInputToTimestamp(filterValues?.periodTo),
        ],
        timezone: filterValues?.timezone || null,
        company: filterValues?.company || null,
        merchant: filterValues?.merchant || null,
        currency: filterValues?.currency || null,
        provider: filterValues?.provider || null,
        merchantAccounts: filterValues?.merchantAccounts || [],
        accountsSummary: filterValues?.accountsSummary || null,
      },
      resetValues: DEFAULT_CUMULATIVE_REPORT_FILTER_VALUES,
    });

  const [showAlert, setShowAlert] = useState(true);

  const isListFilterExist = Object.keys(filterValues).length > 0;

  const summaryListContext = useList({
    data: cumulativeReport?.accountsSummaryItems || [],
    isLoading,
  });
  const reportItemsContext = useList({
    data: cumulativeReport?.reportItems || [],
    isLoading,
  });

  useSyncFilter<CumulativeReportFilterValues>({
    appliedFilters,
    transform: (appliedFilters) => {
      const { appliedDate = [null, null], timezone, ...restFilters } = appliedFilters;
      return cleanEmpty({
        ...restFilters,
        periodFrom: formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
        periodTo: formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        timezone: getAppliedTimezone(timezone, appliedDate),
      });
    },
  });

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton disabled={!data?.length} key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <CumulativeReportFilter />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider
          appliedFilters={appliedFilters}
          onReset={onReset}
          setAppliedFilter={setAppliedFilter}
        >
          <FormProvider {...form}>
            <CumulativeReportListFilterChips />
            <FilterClearButton
              key="clear-filters"
              onClick={() => {
                onReset();
              }}
              visible={isListFilterExist}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
      {cumulativeReport?.reportItems.length && showAlert && (
        <SuccessReportConvertedAlert
          conversionRateType={ConversionRateTypes.Actual}
          currency={filterValues.currency}
          date={{
            gte: filterValues.periodFrom,
            lte: filterValues.periodTo,
          }}
          onClose={() => setShowAlert(false)}
          timezone={filterValues.timezone}
        />
      )}
      {isLoading && !!filterValues.accountsSummary && (
        <CumulativeReportCollapsibleSkeleton columnsCount={11} />
      )}
      {!!cumulativeReport?.accountsSummaryItems?.length && (
        <ListContextProvider value={summaryListContext}>
          <CumulativeReportSummaryCollapsible filters={filterValues} />
        </ListContextProvider>
      )}
      <ListContextProvider value={reportItemsContext}>
        <CumulativeReportDatagridCollapsible
          filters={filterValues}
          isLoading={isLoading}
          toggleDrawer={toggleDrawer}
        />
      </ListContextProvider>
    </>
  );
};
